import { uiSurface } from "shared/ui/uiSurface"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import HistoryIcon from "@mui/icons-material/History"
import dayjs from "dayjs"
import { Dispatch, FC } from "react"
import { useTranslation } from "react-i18next"
import { DashboardOrder, useGetClientOrderSuggestionsQuery } from "../../../apis/appApi"
import type { AddressEntry, OrderSuggestion } from "./useOrderCreateForm"

const formatState = (state: string) =>
  state
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase())

const formatAmount = (amount: number | null): string => {
  if (amount == null || amount === 0) return ""
  return `$${(amount / 100).toFixed(2)}`
}

const formatAddress = (order: DashboardOrder): string => {
  if (!order.address) return ""
  const parts = [
    order.address.streetNumber,
    order.address.streetName,
    order.address.city,
  ].filter(Boolean)
  return parts.join(" ")
}

type Props = {
  clientId: number | null
  addresses: AddressEntry[]
  dispatch: Dispatch<any>
  onFilled?: (reference: string) => void
}

export const RecentOrdersSuggestions: FC<Props> = ({
  clientId,
  addresses,
  dispatch,
  onFilled,
}) => {
  const { t } = useTranslation()

  const { data, isLoading } = useGetClientOrderSuggestionsQuery(
    { clientId: clientId! },
    { skip: !clientId },
  )

  // Don't render anything if no client is selected
  if (!clientId) return null

  const orders = data?.data ?? []

  const handleSelect = (order: DashboardOrder) => {
    const suggestion: OrderSuggestion = {
      addressId: order.addressId,
      amount: order.amount,
      desiredArrivalTime: order.desiredArrivalTime,
      notes: order.notes ?? null,
      tags: order.tags?.map((t) => ({ tagId: t.tagId })),
    }
    dispatch({ type: "FILL_FROM_SUGGESTION", suggestion })
    onFilled?.(order.reference)
  }

  return (
    <Accordion
      {...uiSurface("UI-DLV2-040")}
      defaultExpanded={false}
      disableGutters
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        "&::before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: 40, "& .MuiAccordionSummary-content": { my: 0.5 } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HistoryIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={500}>
            {t("orderCreate.recentOrders")}
          </Typography>
          {orders.length > 0 && (
            <Chip label={orders.length} size="small" sx={{ height: 20, fontSize: 11 }} />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 1 }}>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!isLoading && orders.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1, px: 1 }}>
            {t("orderCreate.recentOrders.empty")}
          </Typography>
        )}
        {!isLoading && orders.length > 0 && (
          <List dense disablePadding>
            {orders.map((order) => (
              <ListItemButton
                key={order.id}
                onClick={() => handleSelect(order)}
                sx={{
                  borderRadius: 1,
                  py: 0.5,
                  px: 1,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace" }}>
                        {order.reference}
                      </Typography>
                      <Chip
                        label={formatState(order.state)}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: 10 }}
                      />
                      {order.amount != null && order.amount > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          {formatAmount(order.amount)}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: "flex", gap: 1, mt: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(order.deliveryDate).format("MMM D, YYYY")}
                      </Typography>
                      {formatAddress(order) && (
                        <Typography variant="caption" color="text.secondary">
                          {formatAddress(order)}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </AccordionDetails>
    </Accordion>
  )
}
